import { resolveProgressTexts } from '@/components/Functional/utils/rewardCondition'
import { Typography } from '@/components/UI/Typography'
import type { RewardOverviewData } from '@/schemas/v2/reward'
import { IconChevronRight } from '@tabler/icons-react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { RewardAcquisitionStatusBadge } from '../RewardAcquisitionStatusBadge'

export type { RewardOverviewData }

type Props = {
  reward: RewardOverviewData
  /** 特典詳細ページへのパス（例: `/uji/rewards/detail?query=1`） */
  href: string
}

export function RewardOverview({ reward, href }: Props) {
  const { t } = useTranslation()
  const conditionTexts = resolveProgressTexts(t, reward.conditions)

  return (
    // 余白は 8px グリッド（左右16px / 上下16px / サムネイルとの間16px）。
    // min-h はサムネイル 100px + 上下 padding 16px × 2 = 132px で、スポット一覧カードと行高を揃える。
    <Link href={href} className="flex min-h-[132px] items-start gap-4 px-4 py-4">
      {/* サムネイル */}
      {reward.image ? (
        <img src={reward.image} alt={reward.name ?? ''} className="size-[100px] shrink-0 rounded-[16px] object-cover" />
      ) : (
        <div className="size-[100px] shrink-0 rounded-[16px] bg-gray-100" aria-hidden="true" />
      )}

      {/* テキスト情報。特典名・状態ラベル・条件テキストの縦間隔は gap:4px に一元管理し、
          子要素に margin を持たせない */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* 特典名: 16px / Bold / 24px。スポット一覧カードのスポット名と同じ階層に揃える */}
        <Typography variant="subtitle1" component="p" c="#000000" className="line-clamp-2">
          {reward.name}
        </Typography>
        {/* 獲得状況バッジ。inline-block を保つため div でラップする（flex 直下だと横幅が伸びる） */}
        {reward.acquisitionStatus && (
          <div>
            <RewardAcquisitionStatusBadge status={reward.acquisitionStatus} />
          </div>
        )}
        {/* 条件テキストが複数行のときも同じ 4px 間隔で積む */}
        {conditionTexts.length > 0 && (
          <div className="flex flex-col gap-1">
            {conditionTexts.map((text, i) => (
              <Typography key={`${i}-${text}`} variant="caption1" component="p" className="text-gray-500">
                {text}
              </Typography>
            ))}
          </div>
        )}
      </div>

      {/* 矢印 */}
      <IconChevronRight size={24} stroke={1.5} className="shrink-0 self-center text-gray-400" aria-hidden="true" />
    </Link>
  )
}
